/**
 * Calendar utility functions for generating calendar event URLs
 * Supports Google Calendar and Outlook calendar integration
 */

/**
 * Format a date for calendar URLs (YYYYMMDDTHHMMSS format)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
function formatCalendarDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Format a date for Outlook calendar (YYYY-MM-DDTHH:MM:SS format)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
function formatOutlookDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().slice(0, 19);
}

/**
 * Generate a Google Calendar event URL
 * @param {Object} event - Event object with title, description, location, start_time, end_time, event_date
 * @returns {string} Google Calendar URL
 */
export function generateGoogleCalendarUrl(event) {
  const baseUrl = 'https://calendar.google.com/calendar/render';
  
  // Determine start and end times
  let startTime, endTime;
  
  if (event.start_time) {
    startTime = formatCalendarDate(event.start_time);
  } else if (event.event_date) {
    startTime = formatCalendarDate(event.event_date);
  } else {
    // Default to now if no date provided
    startTime = formatCalendarDate(new Date());
  }
  
  if (event.end_time) {
    endTime = formatCalendarDate(event.end_time);
  } else {
    // Default to 1 hour after start
    const start = new Date(event.start_time || event.event_date || new Date());
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    endTime = formatCalendarDate(end);
  }
  
  // Build description with truck info
  let description = event.description || '';
  if (event.truck_name || event.username) {
    description = `Food Truck: ${event.truck_name || event.username}\n\n${description}`;
  }
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || 'Food Truck Event',
    dates: `${startTime}/${endTime}`,
    details: description,
    location: event.location || '',
    sf: 'true',
    output: 'xml'
  });
  
  return `${baseUrl}?${params.toString()}`;
}

/**
 * Generate an Outlook calendar event URL (web version)
 * @param {Object} event - Event object with title, description, location, start_time, end_time, event_date
 * @returns {string} Outlook Calendar URL
 */
export function generateOutlookCalendarUrl(event) {
  const baseUrl = 'https://outlook.live.com/calendar/0/deeplink/compose';
  
  // Determine start and end times
  let startTime, endTime;
  
  if (event.start_time) {
    startTime = formatOutlookDate(event.start_time);
  } else if (event.event_date) {
    startTime = formatOutlookDate(event.event_date);
  } else {
    startTime = formatOutlookDate(new Date());
  }
  
  if (event.end_time) {
    endTime = formatOutlookDate(event.end_time);
  } else {
    const start = new Date(event.start_time || event.event_date || new Date());
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    endTime = formatOutlookDate(end);
  }
  
  // Build description with truck info
  let body = event.description || '';
  if (event.truck_name || event.username) {
    body = `Food Truck: ${event.truck_name || event.username}\n\n${body}`;
  }
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title || 'Food Truck Event',
    startdt: startTime,
    enddt: endTime,
    body: body,
    location: event.location || ''
  });
  
  return `${baseUrl}?${params.toString()}`;
}

/**
 * Generate an ICS file download for any calendar app
 * @param {Object} event - Event object
 * @returns {string} Data URL for ICS file download
 */
export function generateICSFile(event) {
  // Determine start and end times
  let startTime, endTime;
  
  if (event.start_time) {
    startTime = formatCalendarDate(event.start_time);
  } else if (event.event_date) {
    startTime = formatCalendarDate(event.event_date);
  } else {
    startTime = formatCalendarDate(new Date());
  }
  
  if (event.end_time) {
    endTime = formatCalendarDate(event.end_time);
  } else {
    const start = new Date(event.start_time || event.event_date || new Date());
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    endTime = formatCalendarDate(end);
  }
  
  // Build description with truck info
  let description = event.description || '';
  if (event.truck_name || event.username) {
    description = `Food Truck: ${event.truck_name || event.username}\\n\\n${description}`;
  }
  // Escape special characters for ICS format
  description = description.replace(/\n/g, '\\n');
  
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AVL Flavor//Food Truck Events//EN',
    'BEGIN:VEVENT',
    `DTSTART:${startTime}`,
    `DTEND:${endTime}`,
    `SUMMARY:${event.title || 'Food Truck Event'}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${event.location || ''}`,
    `UID:${event.id}-avlflavor@avlflavor.com`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
}
